import { Link } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";

// Icons
import { PencilIcon, TrashIcon, } from "@heroicons/react/24/outline";

// Context
import LoaderContext from "../../../context/assessment/LoaderContext";
import SnackbarContext from "../../../context/assessment/SnackbarContext";

// Helper
import errorHandler from "../../../helper/assessment/errorHandler";
import logRender from "../../../helper/assessment/logRender";

// Controller
import LevelAssessment from "../../../controller/assessment/assessment_system/level_assessment";

// Component
import TableFilter from "../../../components/assessment/assessment_system/TableFilter";
import Table from "../../../components/assessment/assessment_system/Table";
import { SnackbarObject } from "../../../context/psikotest/Type";

function List() {
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  const [list, setList] = useState<Array<TLevelAssessmentObject>>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    logRender({ type: "page", name: "level_assessment/List" });
  }, []);

  const getList = useCallback(
    async (setNotif: Dispatch<SetStateAction<SnackbarObject>>) => {
      try {
        setMessage("Fetch Level Assessment");
        const la = new LevelAssessment();
        const res = await la.list();
        setList(res.data.list);
        setMessage("");
      } catch (error) {
        setMessage("");
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [setMessage]
  );

  useEffect(() => {
    getList(setNotif);
  }, [getList, setNotif]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  return (
    <div className="w-full flex flex-col space-y-0.5">
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <Link
          to={`create`}
          className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
        >
          <div className="flex">
            <PencilIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
            Create
          </div>
        </Link>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div>
        <LevelAssessmentList list={list} handleDeleteList={handleDeleteList} search={search} />
      </div>
    </div>
  );
}

function LevelAssessmentList(props: { list: Array<TLevelAssessmentObject>; handleDeleteList: (id: number) => void; search: string }) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (level_assessment: TLevelAssessmentObject) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "Level assessment will be removed, You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const la = new LevelAssessment();
          await la.delete(level_assessment.id.toString());
          props.handleDeleteList(level_assessment.id);
          setNotif({ type: "success", message: "level assessment deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Level",
        accessor: "id",
        show: isMobile,
        Cell: (props: { row: { original: TLevelAssessmentObject } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.level || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "level",
        classHeader: "text-left px-2",
        Header: "Level",
        classDivHeader: "flex items-center",
        accessor: "level",
        show: !isMobile,
        Cell: (props: { row: { original: TLevelAssessmentObject } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.level || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "category",
        classHeader: "text-left px-2",
        Header: "Category",
        classDivHeader: "flex items-center",
        accessor: "level_assessment_category",
        show: !isMobile,
        Cell: (props: { row: { original: TLevelAssessmentObject } }) => {
          return (
            <div className="flex flex-col items-start space-y-1">
              {props.row.original.level_assessment_category.map((category: TLevelAssessmentCategory, i) => (
                <span key={`category_${i}`} className="px-2 rounded text-sm text-white bg-slate-700">
                  {category.category}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TLevelAssessmentObject } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete]
  );

  return <Table columns={columns} list={props.list} search={props.search} />;
}

export default List;
